import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import Image from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import { clsxm } from '@vs-shop/utils';

import type { FramedMediaProps } from '../../internal/framedMedia.tsx';

import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * An article, as a card: a picture, what it is filed under, its headline, when
 * it was written and by whom, and a line of what it says. Flat named parts a
 * tenant arranges in its own JSX, each painting itself.
 *
 *   <ArticleCard variant={ArticleCardVariant.ROW}>
 *     <ArticleCardLayout>
 *       <ArticleCardMediaLink variant={ArticleCardVariant.ROW} href={href} title={title}>
 *         <ArticleCardImage src={src} alt={title} width={457} height={344} />
 *         <ArticleCardBadge render={<Badge variant="primary" />}>{section}</ArticleCardBadge>
 *       </ArticleCardMediaLink>
 *
 *       <ArticleCardBody variant={ArticleCardVariant.ROW}>
 *         <ArticleCardHeader>
 *           <ArticleCardTitleLink href={href} title={title}>
 *             <ArticleCardTitle variant={ArticleCardVariant.ROW}>{title}</ArticleCardTitle>
 *           </ArticleCardTitleLink>
 *           <ArticleCardMeta>…</ArticleCardMeta>
 *         </ArticleCardHeader>
 *
 *         <ArticleCardSummary variant={ArticleCardVariant.ROW}>
 *           <ArticleCardExcerpt variant={ArticleCardVariant.ROW}>{perex}</ArticleCardExcerpt>
 *           <TextLink render={<Link href={href} />}>{readOn}</TextLink>
 *         </ArticleCardSummary>
 *       </ArticleCardBody>
 *     </ArticleCardLayout>
 *   </ArticleCard>
 *
 * **Two arrangements of one card, not two cards.** A listing lays the picture
 * beside the copy in a twelve-column row, and a carousel stacks the copy under
 * the picture; the lead card at the top of a listing is the row again, wider.
 * Which parts a card has and in what order is therefore the composing JSX's —
 * the stacked arrangement composes no {@link ArticleCardLayout}, no
 * {@link ArticleCardHeader} and no {@link ArticleCardSummary}, and an absent
 * part yields no place.
 *
 * **What an article IS does not cross as a prop.** The old card took a `type`
 * enum and a `showType` flag; a badge that says what kind of article this is,
 * and whether there is one at all, is a composition — the tenant renders
 * {@link ArticleCardBadge} or does not, and what the badge says and what colour
 * it is are its own.
 *
 * **No part renders a heading.** A card is furniture a page repeats — a listing
 * of a dozen of them contributing a dozen `<h3>`s of marketing copy writes the
 * page's outline for it — so {@link ArticleCardTitle} is a neutral element and
 * a page that genuinely wants its text in the outline composes
 * `@vs-shop/ui/heading` through the `render` escape.
 *
 * Server-first: every part is a plain server component.
 */

/**
 * How a card is laid out: the picture beside the copy, the same again at the
 * head of a listing where there is room for a wider picture, or the copy under
 * the picture in a carousel place.
 *
 * Required on every part that has the axis, and no recipe declares
 * `defaultVariants`, so no card's arrangement is decided by an omission. The
 * opt-out is the named `unstyled` member.
 */
export const articleCardVariants = cva('', {
  variants: {
    variant: {
      /** Picture beside copy, in a twelve-column row. */
      row: '@container',
      /** The same row, at the head of a listing, where the picture is wider. */
      rowLead: '@container',
      /** Copy under picture, the shape a carousel place holds. */
      stacked: 'grid',
      /** The opt-out: a tenant laying the card out itself. */
      unstyled: '',
    },
  },
});

type ArticleCardVariantName = NonNullable<VariantProps<typeof articleCardVariants>['variant']>;

export type ArticleCardVariant = ArticleCardVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ArticleCardVariant = {
  /** Picture beside copy, in a twelve-column row. */
  ROW: 'row',
  /** The same row, at the head of a listing, where the picture is wider. */
  ROW_LEAD: 'rowLead',
  /** Copy under picture, the shape a carousel place holds. */
  STACKED: 'stacked',
  /** The opt-out: a tenant laying the card out itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ArticleCardVariantName>;

/** Required and closed — not `VariantProps`, which makes every axis optional. */
export type ArticleCardVariantProps = { readonly variant: ArticleCardVariant };

export type ArticleCardProps = ComponentProps<'div'> & ArticleCardVariantProps;

/**
 * The card.
 *
 * The row arrangements carry the container-query context every `@[...]` step in
 * the card resolves against — load-bearing and invisible when missing, so it is
 * the card's rather than a composition's to remember. The stacked arrangement
 * steps on the viewport instead, because a carousel place is a fixed width the
 * rail decides and there is nothing for it to query.
 */
export const ArticleCard = ({ className, variant, ...props }: ArticleCardProps) => (
  <div
    data-slot="article-card"
    data-variant={variant}
    className={clsxm(articleCardVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The twelve-column row the picture and the copy divide between them.
 *
 * Composed only by the row arrangements. The gutter steps on the card's own
 * width, which is why the card renders the container context above it.
 */
const articleCardLayoutClasses = 'grid grid-cols-12 gap-3 @md:gap-6 @4xl:gap-12';

export type ArticleCardLayoutProps = ComponentProps<'div'>;

/** The row's twelve columns. */
export const ArticleCardLayout = ({ className, ...props }: ArticleCardLayoutProps) => (
  <div
    data-slot="article-card-layout"
    className={clsxm(articleCardLayoutClasses, className)}
    {...props}
  />
);

/**
 * The picture, as the link into the article.
 *
 * In a row the anchor IS the frame — it is the grid item, so its column span
 * and its aspect ratio are the same box — while in the stacked arrangement it
 * only establishes the containing block the badge hangs in and
 * {@link ArticleCardMediaFrame} is the box the picture fills.
 */
export const articleCardMediaLinkVariants = cva('', {
  variants: {
    variant: {
      row: 'ring-foreground/10 relative col-span-5 flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg ring @2xl:col-span-4',
      rowLead:
        'ring-foreground/10 relative col-span-12 flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg ring @2xl:col-span-5',
      /** Only the containing block; the frame under it is the box. */
      stacked: 'relative',
      unstyled: '',
    },
  },
});

export type ArticleCardMediaLinkProps = VsLinkProps & ArticleCardVariantProps;

/** The picture's link. */
export const ArticleCardMediaLink = ({
  className,
  variant,
  ...props
}: ArticleCardMediaLinkProps) => (
  <Link
    data-slot="article-card-media-link"
    className={clsxm(articleCardMediaLinkVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The box the stacked arrangement's picture fills.
 *
 * A height rather than only a ratio: a carousel place is as tall as its tallest
 * card, so the pictures have to agree on one height however wide the place is.
 */
const articleCardMediaFrameClasses = 'aspect-[4/3] h-[200px] w-full tab:h-[280px]';

export type ArticleCardMediaFrameProps = ComponentProps<'div'>;

/** The stacked arrangement's picture box. */
export const ArticleCardMediaFrame = ({ className, ...props }: ArticleCardMediaFrameProps) => (
  <div
    data-slot="article-card-media-frame"
    className={clsxm(articleCardMediaFrameClasses, className)}
    {...props}
  />
);

/**
 * The picture's box with no picture in it — the same size and column span, and
 * none of the ring the link around a real picture draws.
 *
 * What stands inside it is the composition's: bars while a card is loading, a
 * missing-image mark where a listing genuinely has no picture to show. Its own
 * part rather than an appearance of {@link ArticleCardMediaLink}, because a box
 * that leads nowhere is not a link.
 */
export const articleCardMediaSkeletonVariants = cva('', {
  variants: {
    variant: {
      row: 'relative col-span-5 flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg @2xl:col-span-4',
      rowLead:
        'relative col-span-12 flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg @2xl:col-span-5',
      stacked: 'tab:h-[280px] aspect-[4/3] h-[200px] w-full',
      unstyled: '',
    },
  },
});

export type ArticleCardMediaSkeletonProps = ComponentProps<'div'> & ArticleCardVariantProps;

/** The picture's box, with no picture in it. */
export const ArticleCardMediaSkeleton = ({
  className,
  variant,
  ...props
}: ArticleCardMediaSkeletonProps) => (
  <div
    data-slot="article-card-media-skeleton"
    className={clsxm(articleCardMediaSkeletonVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The picture itself, filling whichever frame it was composed into.
 *
 * One crop, so it renders the single-image seam — `src` is final and renderable
 * and the CDN/`{filter}` resolution stays app-side (spec § "Media parts").
 */
const articleCardImageClasses = 'h-full w-full rounded-lg object-cover';

export type ArticleCardImageProps = FramedMediaProps;

/** The article's picture. */
export const ArticleCardImage = ({ className, ...props }: ArticleCardImageProps) => (
  <Image
    data-slot="article-card-image"
    className={clsxm(articleCardImageClasses, className)}
    {...props}
  />
);

/**
 * The mark over the picture: which part of the magazine this article is from,
 * or what kind of article it is.
 *
 * Painted through `render`, because WHAT it says and what colour it is are the
 * tenant's — a section name and an article type are the same corner of the same
 * picture and nothing else about them is shared. What the card owns is where it
 * hangs.
 */
const articleCardBadgeClasses = 'absolute top-[5%] left-[5%]';

export type ArticleCardBadgeProps = ComponentProps<'div'> & {
  /** The element to paint — a tenant's badge primitive, with its own colour. */
  readonly render?: RenderElement;
};

/** The picture's corner mark. */
export const ArticleCardBadge = ({ className, render, ...props }: ArticleCardBadgeProps) => (
  <RenderedPart
    dataSlot="article-card-badge"
    className={clsxm(articleCardBadgeClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * Where the stacked arrangement hangs its mark: inset from the picture's corner
 * and shown only once there is room for it.
 *
 * A place rather than a flag — a card with nothing to say about its kind
 * composes no slot at all, instead of an empty box holding the space one would
 * have taken.
 */
const articleCardBadgeSlotClasses = 'top-5 left-4 hidden tab:absolute tab:block';

export type ArticleCardBadgeSlotProps = ComponentProps<'div'>;

/** The stacked arrangement's mark place. */
export const ArticleCardBadgeSlot = ({ className, ...props }: ArticleCardBadgeSlotProps) => (
  <div
    data-slot="article-card-badge-slot"
    className={clsxm(articleCardBadgeSlotClasses, className)}
    {...props}
  />
);

/** The copy column: what the article is called, when it was written, what it says. */
export const articleCardBodyVariants = cva('', {
  variants: {
    variant: {
      row: 'col-span-7 flex flex-col gap-6 @2xl:col-span-8',
      rowLead: 'col-span-12 flex flex-col gap-6 @2xl:col-span-7',
      stacked: 'grid gap-y-3 py-6',
      unstyled: '',
    },
  },
});

export type ArticleCardBodyProps = ComponentProps<'div'> & ArticleCardVariantProps;

/** The card's copy. */
export const ArticleCardBody = ({ className, variant, ...props }: ArticleCardBodyProps) => (
  <div
    data-slot="article-card-body"
    className={clsxm(articleCardBodyVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The headline and the facts under it, kept tighter than the rest of the copy.
 *
 * Composed only by the row arrangements: the stacked card sets one rhythm for
 * its whole column, so a group inside it would be a step it does not take.
 */
const articleCardHeaderClasses = 'flex flex-col gap-4 @2xl:gap-5';

export type ArticleCardHeaderProps = ComponentProps<'div'>;

/** The headline group. */
export const ArticleCardHeader = ({ className, ...props }: ArticleCardHeaderProps) => (
  <div
    data-slot="article-card-header"
    className={clsxm(articleCardHeaderClasses, className)}
    {...props}
  />
);

export type ArticleCardTitleLinkProps = VsLinkProps;

/**
 * The headline's link.
 *
 * It paints nothing: the headline inside it carries the type scale, and the
 * link is the click surface around it. It exists as a part so a composition
 * does not reach for a bare anchor and lose the slot the card is read by.
 */
export const ArticleCardTitleLink = ({ className, ...props }: ArticleCardTitleLinkProps) => (
  <Link
    data-slot="article-card-title-link"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * The headline.
 *
 * A neutral element, and the row arrangement clamps it at two lines: a listing
 * whose cards are each as tall as their longest headline reads as a column of
 * unequal blocks rather than a run.
 */
export const articleCardTitleVariants = cva('', {
  variants: {
    variant: {
      row: 'font-heading text-foreground lap:text-[22px]/[28px] line-clamp-2 text-[22px]/[28px] tracking-tight hover:underline @2xl:text-[26px]/[34px]',
      rowLead:
        'font-heading text-foreground lap:text-[22px]/[28px] line-clamp-2 text-[22px]/[28px] tracking-tight hover:underline @2xl:text-[26px]/[34px]',
      stacked:
        'font-heading text-foreground lap:text-[22px]/[28px] inline text-[18px]/[27px] tracking-tight hover:underline',
      unstyled: '',
    },
  },
});

export type ArticleCardTitleProps = ComponentProps<'div'> &
  ArticleCardVariantProps & {
    /** The element to paint instead of the neutral box — a page's own heading. */
    readonly render?: RenderElement;
  };

/** The article's headline. */
export const ArticleCardTitle = ({
  className,
  variant,
  render,
  ...props
}: ArticleCardTitleProps) => (
  <RenderedPart
    dataSlot="article-card-title"
    className={clsxm(articleCardTitleVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/**
 * The facts under the headline: when it was published, who wrote it, how long
 * it takes to read.
 *
 * Its own container context, because which of the three survive is a question
 * about how wide the ROW is, not how wide the page is — the same card is a full
 * width band on a listing and a third of a carousel place.
 *
 * The marks are the tenant's drawing and the row's size: a call site composes
 * whichever icon set it ships and the parts state how big it renders.
 */
const articleCardMetaClasses =
  '@container flex flex-wrap gap-x-4 gap-y-1.5 text-xs tracking-widest text-foreground-muted uppercase @sm:gap-x-5';

export type ArticleCardMetaProps = ComponentProps<'div'>;

/** The facts row. */
export const ArticleCardMeta = ({ className, ...props }: ArticleCardMetaProps) => (
  <div
    data-slot="article-card-meta"
    className={clsxm(articleCardMetaClasses, className)}
    {...props}
  />
);

/**
 * When it was published — the one fact narrow enough to survive at any width.
 *
 * The three fact recipes state their marks the same way and differ only in when
 * they show. Written out rather than composed from a shared fragment: a class
 * list is a list, and interpolating one splits `hidden`/`@xs:flex` around it.
 */
const articleCardMetaDateClasses =
  'flex items-center gap-2 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-muted';

export type ArticleCardMetaDateProps = ComponentProps<'div'>;

/** The publication date. */
export const ArticleCardMetaDate = ({ className, ...props }: ArticleCardMetaDateProps) => (
  <div
    data-slot="article-card-meta-date"
    className={clsxm(articleCardMetaDateClasses, className)}
    {...props}
  />
);

/** Who wrote it, as the link to the rest of their articles. Dropped on the narrowest cards. */
const articleCardMetaAuthorClasses =
  'hidden items-center gap-2 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-muted @xs:flex';

export type ArticleCardMetaAuthorProps = VsLinkProps;

/** The author. */
export const ArticleCardMetaAuthor = ({ className, ...props }: ArticleCardMetaAuthorProps) => (
  <Link
    data-slot="article-card-meta-author"
    className={clsxm(articleCardMetaAuthorClasses, className)}
    {...props}
  />
);

/** How long it takes to read — the first fact to go, and the last to matter. */
const articleCardMetaReadTimeClasses =
  'hidden items-center gap-2 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-muted @sm:flex';

export type ArticleCardMetaReadTimeProps = ComponentProps<'div'>;

/** The reading time. */
export const ArticleCardMetaReadTime = ({ className, ...props }: ArticleCardMetaReadTimeProps) => (
  <div
    data-slot="article-card-meta-read-time"
    className={clsxm(articleCardMetaReadTimeClasses, className)}
    {...props}
  />
);

/**
 * One fact's words.
 *
 * Unbreakable: a date or a name wrapping mid-word inside a row that already
 * wraps between facts reads as two facts.
 */
const articleCardMetaValueClasses = 'whitespace-nowrap';

export type ArticleCardMetaValueProps = ComponentProps<'span'>;

/** A fact's words. */
export const ArticleCardMetaValue = ({ className, ...props }: ArticleCardMetaValueProps) => (
  <span
    data-slot="article-card-meta-value"
    className={clsxm(articleCardMetaValueClasses, className)}
    {...props}
  />
);

/**
 * What the article says, with the way into it under it.
 *
 * The ordinary row hides the whole group until the card is wide enough to be
 * read rather than scanned; the lead card always shows it. The stacked
 * arrangement composes no group at all — its excerpt is a line in the column.
 */
export const articleCardSummaryVariants = cva('', {
  variants: {
    variant: {
      row: 'hidden flex-col gap-2 @2xl:flex @2xl:gap-3',
      rowLead: 'flex flex-col gap-2 @2xl:gap-3',
      /** The stacked arrangement composes no group; its excerpt stands alone. */
      stacked: '',
      unstyled: '',
    },
  },
});

export type ArticleCardSummaryProps = ComponentProps<'div'> & ArticleCardVariantProps;

/** The excerpt group. */
export const ArticleCardSummary = ({ className, variant, ...props }: ArticleCardSummaryProps) => (
  <div
    data-slot="article-card-summary"
    className={clsxm(articleCardSummaryVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The line or three of what the article says.
 *
 * Clamped at three lines in every arrangement: an excerpt is a taste of the
 * article, and one that runs to a paragraph is the article.
 */
export const articleCardExcerptVariants = cva('line-clamp-3 tracking-tight', {
  variants: {
    variant: {
      row: 'text-foreground-muted text-base/6',
      rowLead: 'text-foreground-muted text-base/6',
      stacked: 'text-foreground',
      unstyled: '',
    },
  },
});

export type ArticleCardExcerptProps = ComponentProps<'p'> & ArticleCardVariantProps;

/** The excerpt. */
export const ArticleCardExcerpt = ({ className, variant, ...props }: ArticleCardExcerptProps) => (
  <p
    data-slot="article-card-excerpt"
    className={clsxm(articleCardExcerptVariants({ variant }), className)}
    {...props}
  />
);
